import React, { useState } from 'react';
import { Copy, Check, Trash2, AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { useApiTokens } from '../hooks/useApiTokens';

interface TokenFormData {
  name: string;
  description: string;
  expiration_days: number;
  scopes: string[];
}

export function ApiTokens() {
  const { tokens, loading, error, createToken, deleteToken, refreshToken } = useApiTokens();
  const [showNewForm, setShowNewForm] = useState(false);
  const [newToken, setNewToken] = useState<TokenFormData>({
    name: '',
    description: '',
    expiration_days: 30,
    scopes: []
  });
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const availableScopes = [
    'alunos:read',
    'alunos:write',
    'metricas:read',
    'email:read',
    'email:write',
    'webhooks:manage'
  ];

  const handleCreateToken = async () => {
    if (!newToken.name || newToken.scopes.length === 0) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      await createToken(newToken);
      setShowNewForm(false);
      setNewToken({
        name: '',
        description: '',
        expiration_days: 30,
        scopes: []
      });
    } catch (error) {
      console.error('Erro ao criar token:', error);
      alert('Erro ao criar token. Por favor, tente novamente.');
    }
  };

  const copyToClipboard = (token: string, id: string) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(id);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-background-card rounded w-1/4"></div>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="h-24 bg-background-card rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Tokens da API</h2>
          <p className="text-sm text-gray-400">Gerencie tokens de acesso à API</p>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="px-4 py-2 bg-[#268c73] text-white rounded-lg flex items-center gap-2 hover:bg-[#1a7a61] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo Token
        </button>
      </div>

      {/* Formulário de novo token */}
      {showNewForm && (
        <div className="bg-background-card p-6 rounded-lg mb-6">
          <h3 className="text-lg font-medium text-white mb-4">Novo Token</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nome</label>
              <input
                type="text"
                value={newToken.name}
                onChange={(e) => setNewToken(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-background text-white rounded-lg px-4 py-2 border border-border focus:border-[#268c73] focus:outline-none"
                placeholder="Ex: Token de Produção"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Descrição (opcional)</label>
              <input
                type="text"
                value={newToken.description}
                onChange={(e) => setNewToken(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-background text-white rounded-lg px-4 py-2 border border-border focus:border-[#268c73] focus:outline-none"
                placeholder="Ex: Token para integração com CRM"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Expiração (dias)</label>
              <input
                type="number"
                value={newToken.expiration_days}
                onChange={(e) => setNewToken(prev => ({ ...prev, expiration_days: parseInt(e.target.value) }))}
                className="w-full bg-background text-white rounded-lg px-4 py-2 border border-border focus:border-[#268c73] focus:outline-none"
                min="1"
                max="365"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Escopos</label>
              <div className="grid grid-cols-2 gap-4">
                {availableScopes.map(scope => (
                  <label key={scope} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newToken.scopes.includes(scope)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewToken(prev => ({
                            ...prev,
                            scopes: [...prev.scopes, scope]
                          }));
                        } else {
                          setNewToken(prev => ({
                            ...prev,
                            scopes: prev.scopes.filter(s => s !== scope)
                          }));
                        }
                      }}
                      className="form-checkbox h-4 w-4 text-[#268c73] bg-background border-border rounded"
                    />
                    <span className="text-gray-300">{scope}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 bg-background text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateToken}
                className="px-4 py-2 bg-[#268c73] text-white rounded-lg hover:bg-[#1a7a61] transition-colors"
              >
                Criar Token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de tokens */}
      <div className="space-y-4">
        {tokens.map((token) => (
          <div key={token.id} className="bg-background-card p-6 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-white">{token.name}</h3>
                {token.description && (
                  <p className="text-sm text-gray-400 mt-1">{token.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => refreshToken(token.id)}
                  className="p-2 bg-background text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
                  title="Renovar token"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Tem certeza que deseja excluir este token?')) {
                      deleteToken(token.id);
                    }
                  }}
                  className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                  title="Excluir token"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 bg-background p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm text-gray-300 font-mono">{token.token}</code>
                <button
                  onClick={() => copyToClipboard(token.token, token.id)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {copiedToken === token.id ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {token.scopes.map(scope => (
                <span
                  key={scope}
                  className="px-2 py-1 bg-[#268c73]/10 text-[#268c73] rounded text-sm"
                >
                  {scope}
                </span>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div>
                <p className="text-sm text-gray-400">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${
                    new Date(token.expires_at) > new Date() ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  <p className="text-white">
                    {new Date(token.expires_at) > new Date() ? 'Ativo' : 'Expirado'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400">Criado em</p>
                <p className="text-white mt-1">
                  {new Date(token.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Expira em</p>
                <p className="text-white mt-1">
                  {new Date(token.expires_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          </div>
        ))}

        {tokens.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">Nenhum token configurado</p>
            <button
              onClick={() => setShowNewForm(true)}
              className="mt-4 text-[#268c73] hover:text-[#1a7a61] transition-colors"
            >
              Criar primeiro token
            </button>
          </div>
        )}
      </div>
    </div>
  );
}